import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCatalogue } from './api';
import { CatalogueIndex } from './types';

export function useCatalogue(
  scope: 'selection' | 'admin',
  sessionId?: number,
  selectedIds?: number[],
  enabled = true,
) {
  const scopeKey = `${scope}:${sessionId ?? ''}`;
  const membershipKey =
    sessionId === undefined
      ? ''
      : [...new Set(selectedIds || [])].sort((a, b) => a - b).join(',');
  const [response, setResponse] = useState<{
    scopeKey: string;
    data: CatalogueIndex;
  } | null>(null);
  const data = response?.scopeKey === scopeKey ? response.data : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const version = useRef(0);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!enabled) return;
    const current = ++version.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchCatalogue(scope, controller.signal, sessionId)
      .then(result => {
        if (!controller.signal.aborted && current === version.current)
          setResponse({ scopeKey, data: result });
      })
      .catch(reason => {
        if (!controller.signal.aborted && current === version.current)
          setError(reason.message);
      })
      .finally(() => {
        if (!controller.signal.aborted && current === version.current)
          setLoading(false);
      });
    return () => controller.abort();
  }, [scope, scopeKey, sessionId, membershipKey, revision, enabled]);
  return { data, loading, error, reload };
}
